import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useStyles } from "./styles";
import clsx from "clsx";
import {
  Button,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";


const EditPointsBalanceForm = ({
  toggleModal,
  currentPoints,
  onSubmitPoints,
  accessHolderID,
  accessHolderName,
}) => {
  const classes = useStyles();

  const schema = Yup.object().shape({
    points: Yup.number()
      .typeError("Must be a number")
      .integer("Must be a whole number")
      .min(0, "Must be 0 or greater")
      .required("Required"),
    note: Yup.string()
      .max(200, "Must be less than 200 characters")
      .required("Required"),
  });

  const { control, handleSubmit, reset, watch, formState: { isSubmitting, isValid, errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { points: currentPoints ?? 0, note: "" },
    mode: "onChange",
  });

  useEffect(() => {
    reset({ points: currentPoints ?? 0, note: "" });
  }, []);

  const watchedPoints = watch("points");
  const parsedPoints = parseInt(watchedPoints, 10);
  const current = currentPoints ?? 0;
  const newPoints = !isNaN(parsedPoints) && parsedPoints >= 0 ? parsedPoints : null;

  const firstName = typeof accessHolderName === "object" ? (accessHolderName?.first ?? "") : "";
  const lastName = typeof accessHolderName === "object" ? (accessHolderName?.last ?? "") : "";

  const calculateMetadata = (points, note, adjustmentType) => {
    const prevPointBalance = currentPoints ?? 0;
    const delta = adjustmentType === "Credit" ? points : -points;
    const changes = {
      accessHolderID,
      accessHolderName: `${firstName} ${lastName}`.trim(),
      prevPointBalance,
      UpdatedPointBalance: prevPointBalance + delta,
      Notes: note,
    };
    return JSON.stringify(changes);
  };

  const onSubmit = (data) => {
    const { points, note } = data;
    const newTotal = Number(points);
    const delta = newTotal - current;

    if (delta === 0) {
      toggleModal();
      return;
    }

    const adjustmentType = delta > 0 ? "Credit" : "Debit";
    const metadata = calculateMetadata(Math.abs(delta), note, adjustmentType);

    onSubmitPoints({ points: Math.abs(delta), type: adjustmentType, note, metadata, firstName, lastName });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={clsx("form-content", classes.contentRoot)}>
        <Grid
          container
          className={clsx("points-container", classes.amountContainer)}
        >
          <Grid item xs={12}>
            <Controller
              name="points"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Points"
                  {...field}
                  error={!!errors.points}
                  helperText={errors.points?.message}
                  type="text"
                  variant="outlined"
                  inputProps={{ "data-testid": "edit-points-amount" }}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container className={clsx("comment-container", classes.commentContainer)}>
          <Grid item xs={12}>
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  label="Comment"
                  {...field}
                  error={!!errors.note}
                  helperText={errors.note?.message}
                  type="text"
                  variant="outlined"
                  inputProps={{ "data-testid": "edit-points-comment" }}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container className={clsx("points-update-container", classes.pointsAdjustmentContainer)}>
          <Typography
            data-testid="points-update-text"
            className={clsx(
              "points-update-text",
              newPoints !== null ? classes.showPoints : classes.hidePoints
            )}
          >
            New points will be {newPoints}
          </Typography>
        </Grid>
        <Grid container className={clsx("button-container", classes.buttonContainer)}>
          <Grid item>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSubmitting || !isValid}
              data-testid="edit-points-submit-button"
            >
              Submit
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={toggleModal}
              color="secondary"
              variant="contained"
              className={clsx("edit-points-cancel-button", classes.cancelButton)}
              disabled={isSubmitting}
              data-testid="edit-points-cancel-button"
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </div>
    </form>
  );
};

EditPointsBalanceForm.propTypes = {
  toggleModal: PropTypes.func,
  currentPoints: PropTypes.number,
  onSubmitPoints: PropTypes.func,
  accessHolderID: PropTypes.string,
  accessHolderName: PropTypes.shape({
    first: PropTypes.string,
    last: PropTypes.string,
  }),
};

export default EditPointsBalanceForm;
